import React, { useState, useRef, useEffect } from 'react';
import { Mic, MicOff, StopCircle, Play, Trash2, Loader2, Check, AlertCircle, Settings } from 'lucide-react';
import { Button } from '../ui/button';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * AudioNoteRecorder - Records audio, transcribes with Whisper AI, and returns text
 * Props:
 *   - onTranscriptionComplete: (text) => void - Called when transcription is ready
 *   - existingText: string - Pre-fill with existing note text
 *   - disabled: boolean - Disable the recorder
 */
const AudioNoteRecorder = ({ onTranscriptionComplete, existingText = '', disabled = false }) => {
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [audioBlob, setAudioBlob] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [transcription, setTranscription] = useState(existingText);
  const [error, setError] = useState('');
  const [micPermission, setMicPermission] = useState('unknown'); // 'granted', 'denied', 'prompt', 'unknown'
  const [isCheckingMic, setIsCheckingMic] = useState(false);
  
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const timerRef = useRef(null);
  const streamRef = useRef(null);

  // Check microphone permission on mount
  useEffect(() => {
    checkMicrophonePermission();
  }, []);

  // Check microphone permission status
  const checkMicrophonePermission = async () => {
    try {
      if (navigator.permissions && navigator.permissions.query) {
        const result = await navigator.permissions.query({ name: 'microphone' });
        setMicPermission(result.state);
        
        // Listen for permission changes
        result.onchange = () => {
          setMicPermission(result.state);
        };
      } else {
        // Fallback for browsers that don't support permissions API
        setMicPermission('unknown');
      }
    } catch (err) {
      console.log('Permission check not supported:', err);
      setMicPermission('unknown');
    }
  };

  // Request microphone access
  const requestMicrophoneAccess = async () => {
    setIsCheckingMic(true);
    setError('');
    
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      // Successfully got access - stop the stream immediately
      stream.getTracks().forEach(track => track.stop());
      setMicPermission('granted');
      toast.success('Microphone access granted!');
    } catch (err) {
      console.error('Microphone access error:', err);
      if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
        setMicPermission('denied');
        setError('Microphone access was denied. Please enable it in your browser settings.');
      } else if (err.name === 'NotFoundError') {
        setError('No microphone found. Please connect a microphone and try again.');
      } else {
        setError(`Microphone error: ${err.message}`);
      }
    } finally {
      setIsCheckingMic(false);
    }
  };

  // Open browser settings (provides instructions)
  const openMicSettings = () => {
    const isChrome = navigator.userAgent.includes('Chrome');
    const isSafari = navigator.userAgent.includes('Safari') && !navigator.userAgent.includes('Chrome');
    const isFirefox = navigator.userAgent.includes('Firefox');
    
    let instructions = '';
    if (isChrome) {
      instructions = 'Click the lock/tune icon in the address bar → Site settings → Microphone → Allow';
    } else if (isSafari) {
      instructions = 'Safari → Settings → Websites → Microphone → Allow for this site';
    } else if (isFirefox) {
      instructions = 'Click the lock icon → Permissions → Allow Microphone';
    } else {
      instructions = 'Check your browser settings to allow microphone access for this site';
    }
    
    toast.info(instructions, { duration: 8000 });
  };

  // Format time display
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Start recording
  const startRecording = async () => {
    try {
      setError('');
      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: { echoCancellation: true, noiseSuppression: true, sampleRate: 44100 }
      });
      
      // Permission was granted
      setMicPermission('granted');
      streamRef.current = stream;
      
      const mimeType = MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm' : 'audio/mp4';
      const mediaRecorder = new MediaRecorder(stream, { mimeType });
      
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];
      
      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };
      
      mediaRecorder.onstop = () => {
        const blob = new Blob(audioChunksRef.current, { type: mediaRecorder.mimeType });
        setAudioBlob(blob);
        setAudioUrl(URL.createObjectURL(blob));
        stream.getTracks().forEach(track => track.stop());
      };
      
      mediaRecorder.start(1000);
      setIsRecording(true);
      
      timerRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);
      
    } catch (err) {
      console.error('Recording error:', err);
      if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
        setMicPermission('denied');
        setError('Microphone access denied. Click "How to enable" for instructions.');
      } else if (err.name === 'NotFoundError') {
        setError('No microphone found. Please connect a microphone.');
      } else {
        setError(`Recording error: ${err.message}`);
      }
    }
  };

  // Stop recording
  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      clearInterval(timerRef.current);
      
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
    }
  };

  // Clear recording
  const clearRecording = () => {
    setAudioBlob(null);
    setAudioUrl(null);
    setRecordingTime(0);
    setTranscription(existingText);
    audioChunksRef.current = [];
  };

  // Play recorded audio
  const playRecording = () => {
    if (audioUrl) {
      const audio = new Audio(audioUrl);
      audio.play();
    }
  };

  // Transcribe audio
  const transcribeAudio = async () => {
    if (!audioBlob) {
      setError('No audio to transcribe');
      return;
    }
    
    setIsTranscribing(true);
    setError('');
    
    try {
      const token = localStorage.getItem('rooftrack_token');
      const formData = new FormData();
      formData.append('file', audioBlob, 'recording.webm');
      
      const response = await axios.post(
        `${BACKEND_URL}/api/crm/transcribe-note`,
        formData,
        { 
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      
      const transcribedText = response.data.transcription;
      setTranscription(transcribedText);
      
      // Notify parent component
      if (onTranscriptionComplete) {
        onTranscriptionComplete(transcribedText);
      }
      
      toast.success('Audio transcribed successfully');
      
    } catch (err) {
      console.error('Transcription error:', err);
      setError(err.response?.data?.detail || 'Transcription failed. Please try again.');
      toast.error('Transcription failed');
    } finally {
      setIsTranscribing(false);
    }
  };

  // Handle text change (manual editing)
  const handleTextChange = (e) => {
    const newText = e.target.value;
    setTranscription(newText);
    if (onTranscriptionComplete) {
      onTranscriptionComplete(newText);
    }
  };

  return (
    <div className="space-y-3" data-testid="audio-note-recorder">
      {/* Microphone Status Indicator */}
      <div className="flex items-center gap-2 text-sm">
        {micPermission === 'granted' && (
          <span className="flex items-center gap-1 text-green-600 bg-green-50 px-2 py-1 rounded">
            <Mic className="w-3 h-3" />
            Microphone ready
          </span>
        )}
        {micPermission === 'denied' && (
          <div className="flex items-center gap-2">
            <span className="flex items-center gap-1 text-red-600 bg-red-50 px-2 py-1 rounded">
              <MicOff className="w-3 h-3" />
              Microphone blocked
            </span>
            <button
              onClick={openMicSettings}
              className="text-blue-600 hover:text-blue-700 underline text-xs flex items-center gap-1"
            >
              <Settings className="w-3 h-3" />
              How to enable
            </button>
          </div>
        )}
        {(micPermission === 'prompt' || micPermission === 'unknown') && (
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={requestMicrophoneAccess}
            disabled={isCheckingMic}
            className="text-xs"
            data-testid="enable-microphone"
          >
            {isCheckingMic ? (
              <Loader2 className="w-3 h-3 mr-1 animate-spin" />
            ) : (
              <Mic className="w-3 h-3 mr-1" />
            )}
            {isCheckingMic ? 'Checking...' : 'Enable Microphone'}
          </Button>
        )}
      </div>

      {/* Recording Controls */}
      <div className="flex items-center gap-2">
        {!isRecording && !audioBlob && (
          <Button
            type="button"
            size="sm"
            onClick={startRecording}
            disabled={disabled || micPermission === 'denied'}
            className="bg-red-500 hover:bg-red-600 text-white"
            data-testid="start-audio-recording"
          >
            <Mic className="w-4 h-4 mr-1" />
            Record Note
          </Button>
        )}
        
        {isRecording && (
          <>
            <div className="flex items-center gap-2 px-3 py-1.5 bg-red-100 rounded-lg">
              <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></div>
              <span className="text-red-700 font-mono text-sm">{formatTime(recordingTime)}</span>
            </div>
            <Button
              type="button"
              size="sm"
              onClick={stopRecording}
              className="bg-gray-700 hover:bg-gray-600 text-white"
              data-testid="stop-audio-recording"
            >
              <StopCircle className="w-4 h-4 mr-1" />
              Stop
            </Button>
          </>
        )}
        
        {audioBlob && !isRecording && (
          <>
            <div className="flex items-center gap-1 px-2 py-1 bg-green-100 rounded-lg">
              <Check className="w-4 h-4 text-green-600" />
              <span className="text-green-700 text-sm">{formatTime(recordingTime)}</span>
            </div>
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={playRecording}
              data-testid="play-audio-recording"
            >
              <Play className="w-4 h-4" />
            </Button>
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={clearRecording}
              className="text-red-500 border-red-300 hover:bg-red-50"
              data-testid="clear-audio-recording"
            >
              <Trash2 className="w-4 h-4" />
            </Button>
            <Button
              type="button"
              size="sm"
              onClick={transcribeAudio}
              disabled={isTranscribing}
              className="bg-blue-500 hover:bg-blue-600 text-white"
              data-testid="transcribe-audio"
            >
              {isTranscribing ? (
                <>
                  <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                  Transcribing...
                </>
              ) : (
                <>
                  <Mic className="w-4 h-4 mr-1" />
                  Transcribe
                </>
              )}
            </Button>
          </>
        )}
      </div>
      
      {/* Error message */}
      {error && (
        <div className="text-sm text-red-600 bg-red-50 px-3 py-2 rounded-lg">
          {error}
        </div>
      )}
      
      {/* Transcription/Note textarea */}
      <textarea
        value={transcription}
        onChange={handleTextChange}
        placeholder="Record audio and transcribe, or type your note here..."
        className="w-full border rounded-md px-3 py-2 h-24 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-blue-500"
        disabled={disabled || isTranscribing}
        data-testid="note-textarea"
      />
      
      <p className="text-xs text-gray-500">
        Record an audio note and click "Transcribe" to convert to text, or type directly.
      </p>
    </div>
  );
};

export default AudioNoteRecorder;
